/* ═══════════════════ the clock, and what is behind it ═══════════════════

   The landing page: the time over your own photograph, and under it whichever
   of three things is worth saying — the running task, the pomodoro, or how
   much is open.

   Backgrounds are the only large thing studyHub stores, which is why uploads
   are downscaled before they are kept and why they live under a storage key of
   their own: a full image quota must never be able to corrupt the term. */
import { standingMultiple } from '../state/evidence.js';
import { pad, uid } from '../core/time.js';
import { IMAGES, S, removeImage, save, saveImages } from '../state/store.js';
import { renderCleanClock } from './board.js';
import { el, toast } from './dom.js';
import { pomoOn, pomoWorkLeft } from './tasks.js';
import { $ } from './dom.js';
import { elapsedSec } from '../core/domain.js';
import { DAY_MS, escapeHtml, fmtClock, fmtDurMin, startOfDay } from '../core/time.js';
import { pomoLeft } from './tasks.js';
import { PLAN_REV, WEEK, weekPlanIsCurrent, wkStart } from '../state/plans.js';
import { liveBlocks } from './weekly.js';
import { fmtMinOfDay } from './format.js';

export function renderClock() {
  const now = new Date();
  let h = now.getHours();
  const m = pad(now.getMinutes());
  const s = pad(now.getSeconds());
  let ampm = '';
  // A separate name for the text: one branch wants a number and the other a
  // zero-padded string, and only the text is used from here on.
  let hh;
  if (S.clock.format === '12') {
    ampm = h >= 12 ? 'PM' : 'AM';
    hh = String(h % 12 || 12);
  } else {
    hh = pad(h);
  }
  const parts = [`${hh}:${m}`];
  if (S.clock.seconds) parts.push(`<span class="sec">${s}</span>`);
  if (ampm) parts.push(`<span class="ampm">${ampm}</span>`);
  $('clockTime').innerHTML = parts.join('');
  $('clockDate').textContent = now.toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
  renderCleanClock();   // the clean board shows the same time on the same tick

  // running task read-out under the clock
  const run = S.tasks.find((t) => t.runSince);
  const sub = $('clockSub');
  const onBreak = pomoOn() && (S.pomo.phase === 'break' || S.pomo.phase === 'long');
  if (onBreak) {
    sub.innerHTML = `${S.pomo.phase === 'long' ? '🌿 Long break' : '☕ Break'} · <b>${fmtClock(pomoLeft() / 1000)}</b> left `
      + `· ${S.pomo.done} pomodoro${S.pomo.done === 1 ? '' : 's'} done`;
    sub.hidden = false;
  } else if (run) {
    const left = pomoWorkLeft();
    sub.innerHTML = `▶ ${escapeHtml(run.title)} · <b>${fmtClock(elapsedSec(run))}</b> of ${fmtDurMin(run.estMin)} estimated`
      + (left == null ? '' : ` · 🍅 <b>${fmtClock(left)}</b> left`);
    sub.hidden = false;
  } else {
    sub.innerHTML = idleSub(now);
    sub.hidden = false;
  }
}

/* ─────────────────── what the idle clock says ───────────────────

   A read-out of a computation that already ran, which is the whole of why it
   is safe: it cannot lie, and it has no upkeep. It must never reach
   `recomputePlans()` — this runs on a 500 ms interval and that path sweeps
   block references and can `save()` — so it reads `WEEK` as it stands and says
   nothing at all when the plan is stale.

   Three silences are kept apart on purpose. Nothing written down anywhere is a
   different state from nothing planned this week, which is different again
   from a week that was planned and has nothing left in today. Collapsing them
   would make the line useless exactly when it is most wanted. */

/** The string is rebuilt on the minute, or when anything the planner watches
    moves — not sixty times a minute at 500 ms a tick. */
let subMemo = { key: '', html: '' };

function idleSub(now) {
  const key = `${Math.floor(now.getTime() / 60000)}:${PLAN_REV}`;
  if (subMemo.key === key) return subMemo.html;
  const html = buildIdleSub(now);
  subMemo = { key, html };
  return html;
}

function buildIdleSub(now) {
  /* Nothing anywhere takes priority over everything below, and clears itself
     the moment one thing is written down — so it needs no "seen" key. */
  if (!S.tasks.length && !S.weekly.items.length && !S.longterm.entries.length) {
    return 'nothing written down yet. press ＋ new for one task, or lay out the week on the '
      + 'weekly tab — the plan needs to know what a week is made of before it can say when '
      + 'anything goes.';
  }

  const active = S.tasks.filter((t) => !t.done).length;
  const mult = standingMultiple();
  const openTasks = active
    ? `${active} open task${active === 1 ? '' : 's'}${mult ? ` · standing multiple <b>${mult.toFixed(2)}×</b>` : ''}`
    : 'No open tasks';

  // No plan, or last week's: the line it has always shown, unchanged.
  if (!weekPlanIsCurrent()) return openTasks;

  const todayIdx = Math.floor((startOfDay(now).getTime() - wkStart()) / DAY_MS);
  const today = liveBlocks({ unfiltered: true })
    .filter((b) => b.dayIdx === todayIdx)
    .sort((a, b) => a.startMin - b.startMin);
  if (!today.length) return `nothing planned into today · ${openTasks}`;

  /* The day's total comes off the fold rather than being re-added here, so the
     clock and the day dock quote one number worked out once. */
  const fold = (WEEK.days || []).find((d) => d.dayIdx === todayIdx);
  const planned = fmtDurMin(fold ? fold.placedMin : today.reduce((a, b) => a + (b.endMin - b.startMin), 0));

  const nowMin = now.getHours() * 60 + now.getMinutes();
  const ahead = today.filter((b) => b.endMin > nowMin && !b.missed);
  if (!ahead.length) {
    return `${today.length} block${today.length === 1 ? '' : 's'} today, all of them behind you · ${planned} planned`;
  }

  const next = ahead[0];
  const after = ahead.length - 1;
  return `next: ${escapeHtml(next.title)} · ${fmtMinOfDay(next.startMin)}–${fmtMinOfDay(next.endMin)}`
    + (after ? ` · ${after} more after it` : '')
    + ` · ${planned} planned today`;
}

export function applyBackground() {
  const img = IMAGES.find((i) => i.id === S.bg.activeId);
  const bg = $('clockBg');

  // `has-image` swaps the clock from ink-on-paper to light-on-scrim
  $('clockStage').classList.toggle('has-image', !!img);

  if (img) {
    bg.style.backgroundImage = `url("${img.url}")`;
    bg.style.backgroundSize = S.bg.fit === 'repeat' ? 'auto' : (S.bg.fit === 'center' ? 'auto' : S.bg.fit);
    bg.style.backgroundRepeat = S.bg.fit === 'repeat' ? 'repeat' : 'no-repeat';
  } else {
    // clear the inline declarations rather than setting `none`, so the frame
    // goes back to being an empty rule around the clock with the page ground
    // showing through it
    bg.style.backgroundImage = '';
    bg.style.backgroundSize = '';
    bg.style.backgroundRepeat = '';
  }
  bg.style.filter = img ? `blur(${S.bg.blur}px)` : '';
  bg.style.transform = img && S.bg.blur > 0 ? 'scale(1.04)' : 'none';   // hide blurred edges
  $('clockScrim').style.background = img
    ? `linear-gradient(rgba(20,17,12,${S.bg.dim / 100}), rgba(20,17,12,${Math.min(0.95, S.bg.dim / 100 + 0.12)}))`
    : '';

  $('bgDim').value = S.bg.dim;   $('dimOut').textContent = S.bg.dim + '%';
  $('bgBlur').value = S.bg.blur; $('blurOut').textContent = S.bg.blur + 'px';
  $('bgFit').value = S.bg.fit;
  $('bgShuffle').checked = !!S.bg.shuffle;
  $('clockFormat').value = S.clock.format;
  $('showSeconds').checked = !!S.clock.seconds;
}

export function renderGallery() {
  const g = $('bgGallery');
  g.textContent = '';

  const none = el('button', 'bg-none' + (S.bg.activeId ? '' : ' is-active'), 'none');
  none.type = 'button';
  none.title = 'No background';
  none.addEventListener('click', () => { S.bg.activeId = null; save(); applyBackground(); renderGallery(); });
  g.appendChild(none);

  IMAGES.forEach((img) => {
    const t = el('div', 'bg-thumb' + (S.bg.activeId === img.id ? ' is-active' : ''));
    t.style.backgroundImage = `url("${img.url}")`;
    t.title = img.name;
    t.addEventListener('click', () => {
      S.bg.activeId = img.id; save(); applyBackground(); renderGallery();
    });
    const x = el('button', 'thumb-x', '✕');
    x.title = 'Delete';
    x.addEventListener('click', (e) => {
      e.stopPropagation();
      removeImage(img.id);
      if (S.bg.activeId === img.id) S.bg.activeId = IMAGES.length ? IMAGES[0].id : null;
      saveImages(); save(); applyBackground(); renderGallery();
    });
    t.appendChild(x);
    g.appendChild(t);
  });
}

/** Downscale big uploads so several images still fit in localStorage. */
export function fileToStoredImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => {
      const dataUrl = /** @type {string} */ (reader.result);
      const img = new Image();
      img.onerror = () => reject(new Error('not an image'));
      img.onload = () => {
        const MAX = 2200;
        const small = file.size < 500 * 1024 && img.width <= MAX;
        if (small) return resolve({ id: uid(), name: file.name, url: dataUrl });

        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const c = document.createElement('canvas');
        c.width = Math.round(img.width * scale);
        c.height = Math.round(img.height * scale);
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0, c.width, c.height);
        // JPEG keeps stored size sane; backgrounds never need an alpha channel
        resolve({ id: uid(), name: file.name, url: c.toDataURL('image/jpeg', 0.86) });
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

async function handleFiles(fileList) {
  const files = [...fileList].filter((f) => /^image\//.test(f.type));
  if (!files.length) return toast('Those files are not images.', true);
  for (const f of files) {
    try {
      const rec = await fileToStoredImage(f);
      IMAGES.push(rec);
      if (!saveImages()) { IMAGES.pop(); return; }
      S.bg.activeId = rec.id;
    } catch (e) { toast(`Could not read ${f.name}`, true); }
  }
  save(); applyBackground(); renderGallery();
  toast(files.length === 1 ? 'Background added' : `${files.length} backgrounds added`);
}

export function initClockTab() {
  $('bgHandle').addEventListener('click', () => {
    $('bgDrawer').hidden = false; $('bgHandle').hidden = true;
  });
  $('bgClose').addEventListener('click', () => {
    $('bgDrawer').hidden = true; $('bgHandle').hidden = false;
  });

  const zone = $('uploadZone'), input = $('bgFile');
  zone.addEventListener('click', () => input.click());
  input.addEventListener('change', () => { handleFiles(input.files); input.value = ''; });
  ['dragenter', 'dragover'].forEach((ev) =>
    zone.addEventListener(ev, (e) => { e.preventDefault(); zone.classList.add('is-over'); }));
  ['dragleave', 'drop'].forEach((ev) =>
    zone.addEventListener(ev, () => zone.classList.remove('is-over')));
  zone.addEventListener('drop', (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); });

  // dropping anywhere on the clock stage works too
  const stage = $('clockStage');
  stage.addEventListener('dragover', (e) => e.preventDefault());
  stage.addEventListener('drop', (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); });

  $('bgDim').addEventListener('input', (e) => { S.bg.dim = +e.target.value; applyBackground(); save(); });
  $('bgBlur').addEventListener('input', (e) => { S.bg.blur = +e.target.value; applyBackground(); save(); });
  $('bgFit').addEventListener('change', (e) => { S.bg.fit = e.target.value; applyBackground(); save(); });
  $('bgShuffle').addEventListener('change', (e) => { S.bg.shuffle = e.target.checked; save(); });
  $('clockFormat').addEventListener('change', (e) => { S.clock.format = e.target.value; renderClock(); save(); });
  $('showSeconds').addEventListener('change', (e) => { S.clock.seconds = e.target.checked; renderClock(); save(); });

  // rotate backgrounds
  setInterval(() => {
    if (!S.bg.shuffle || IMAGES.length < 2) return;
    const i = IMAGES.findIndex((x) => x.id === S.bg.activeId);
    S.bg.activeId = IMAGES[(i + 1) % IMAGES.length].id;
    save(); applyBackground(); renderGallery();
  }, 5 * 60 * 1000);
}
